'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { ArrowLeft, FileText, Download, Printer, Eye, Filter, Bird, BarChart3, ArrowRightLeft, Skull, ShoppingCart, Scale, XCircle } from 'lucide-react';
import { Card } from '@/components/ui/Cards';
import { MOCK_FLOCK_SUMMARIES, FARMS } from '../../components/birdRecordsData';

interface ReportDefinition {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  iconBg: string;
  iconColor: string;
  category: string;
}

const REPORTS: ReportDefinition[] = [
  { id: 'daily-population', name: 'Daily Bird Population Report', description: 'Opening, movements, and closing population by flock for a specific date', icon: Bird, iconBg: '#eff6ff', iconColor: '#2563eb', category: 'Population' },
  { id: 'weekly-population', name: 'Weekly Population Report', description: 'Week-by-week population summary across all flocks', icon: Bird, iconBg: '#eff6ff', iconColor: '#2563eb', category: 'Population' },
  { id: 'monthly-population', name: 'Monthly Population Report', description: 'Monthly population changes and closing balances', icon: Bird, iconBg: '#eff6ff', iconColor: '#2563eb', category: 'Population' },
  { id: 'flock-population', name: 'Flock Population Report', description: 'Complete population history for a specific flock', icon: BarChart3, iconBg: '#f0fdf4', iconColor: '#15803d', category: 'Population' },
  { id: 'house-population', name: 'House Population Report', description: 'Current and historical population by poultry house', icon: BarChart3, iconBg: '#f0fdf4', iconColor: '#15803d', category: 'Population' },
  { id: 'bird-movement', name: 'Bird Movement Report', description: 'Full movement ledger with all population-changing transactions', icon: ArrowRightLeft, iconBg: '#fff7ed', iconColor: '#d97706', category: 'Movements' },
  { id: 'mortality', name: 'Mortality Report', description: 'Mortality records with causes, rates and trends', icon: Skull, iconBg: '#fef2f2', iconColor: '#dc2626', category: 'Movements' },
  { id: 'culling', name: 'Culling Report', description: 'Culling records with reasons and authorized approvals', icon: XCircle, iconBg: '#fef2f2', iconColor: '#b91c1c', category: 'Movements' },
  { id: 'transfer', name: 'Transfer Report', description: 'Bird transfers between houses, farms and branches', icon: ArrowRightLeft, iconBg: '#fff7ed', iconColor: '#d97706', category: 'Movements' },
  { id: 'bird-sales', name: 'Bird Sales Movement Report', description: 'Sales-linked bird population reductions', icon: ShoppingCart, iconBg: '#faf5ff', iconColor: '#9333ea', category: 'Movements' },
  { id: 'acquisition', name: 'Bird Acquisition Report', description: 'Purchases, placements and hatch records', icon: Bird, iconBg: '#f0fdf4', iconColor: '#059669', category: 'Movements' },
  { id: 'adjustment', name: 'Population Adjustment Report', description: 'All adjustments with reasons and approvals', icon: Scale, iconBg: '#fffbeb', iconColor: '#d97706', category: 'Adjustments' },
  { id: 'reconciliation', name: 'Reconciliation Report', description: 'Physical count vs system count variances', icon: Scale, iconBg: '#fffbeb', iconColor: '#d97706', category: 'Adjustments' },
  { id: 'survival', name: 'Survival Report', description: 'Survival rates and mortality metrics by flock and period', icon: BarChart3, iconBg: '#f0fdf4', iconColor: '#15803d', category: 'Analytics' },
  { id: 'variance', name: 'Population Variance Report', description: 'Unexplained variances and data quality issues', icon: BarChart3, iconBg: '#fef2f2', iconColor: '#dc2626', category: 'Analytics' },
];

const CATEGORIES = ['All', 'Population', 'Movements', 'Adjustments', 'Analytics'];

export default function BirdReportsContent() {
  const [category, setCategory] = useState('All');
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [farm, setFarm] = useState('All Farms');
  const [dateFrom, setDateFrom] = useState('2026-09-01');
  const [dateTo, setDateTo] = useState('2026-09-06');

  const filtered = REPORTS.filter(r => category === 'All' || r.category === category);
  const activeReport = REPORTS.find(r => r.id === selectedReport);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Link href="/bird-records" className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground transition-colors">
            <ArrowLeft size={14} />
          </Link>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Bird Records Reports</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Generate, view and export bird population reports</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Report List */}
        <div className="lg:col-span-1 space-y-3">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-1">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${category === c ? 'bg-primary text-primary-foreground' : 'border border-border/60 text-muted-foreground hover:bg-muted/50'}`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Report Cards */}
          <div className="space-y-2">
            {filtered.map((report) => (
              <button
                key={report.id}
                onClick={() => setSelectedReport(report.id)}
                className={`w-full text-left p-3 rounded-xl border transition-all duration-200 ${selectedReport === report.id ? 'border-primary/40 bg-primary/5' : 'border-border/60 bg-card hover:shadow-sm hover:border-border'}`}
              >
                <div className="flex items-start gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: report.iconBg }}>
                    <report.icon size={14} style={{ color: report.iconColor }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-foreground leading-tight">{report.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-tight">{report.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Report Preview / Generator */}
        <div className="lg:col-span-2">
          {!selectedReport ? (
            <Card padding="md" className="h-full flex items-center justify-center min-h-[300px]">
              <div className="text-center">
                <FileText size={32} className="mx-auto text-muted-foreground/40 mb-2" />
                <p className="text-sm text-muted-foreground">Select a report to configure and generate</p>
              </div>
            </Card>
          ) : (
            <div className="space-y-3">
              {/* Report Config */}
              <Card padding="md">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">{activeReport?.name}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{activeReport?.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">Date From</label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">Date To</label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">Farm</label>
                    <select
                      value={farm}
                      onChange={(e) => setFarm(e.target.value)}
                      className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
                    >
                      <option>All Farms</option>
                      {FARMS.map(f => <option key={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">Flock</label>
                    <select className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40">
                      <option>All Flocks</option>
                      {MOCK_FLOCK_SUMMARIES.map(f => <option key={f.flockId}>{f.flockNumber}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">Bird Type</label>
                    <select className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40">
                      <option>All Types</option>
                      <option>Broiler</option>
                      <option>Layer</option>
                      <option>Breeder</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground block mb-1">Status</label>
                    <select className="w-full text-xs border border-border/60 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-primary/40">
                      <option>All Statuses</option>
                      <option>Posted</option>
                      <option>Approved</option>
                      <option>Pending</option>
                    </select>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-border/60">
                  <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                    <Eye size={12} />
                    Generate Report
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border/60 rounded-lg bg-card hover:bg-muted/50 transition-colors">
                    <Download size={12} />
                    Export PDF
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border/60 rounded-lg bg-card hover:bg-muted/50 transition-colors">
                    <Download size={12} />
                    Export Excel
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-border/60 rounded-lg bg-card hover:bg-muted/50 transition-colors">
                    <Printer size={12} />
                    Print
                  </button>
                </div>
              </Card>

              {/* Report Preview */}
              {selectedReport === 'daily-population' && (
                <Card padding="sm">
                  <div className="text-center mb-4">
                    <p className="text-sm font-bold text-foreground">Y77 POULTRY</p>
                    <p className="text-xs font-semibold text-foreground">DAILY BIRD POPULATION REPORT</p>
                    <p className="text-xs text-muted-foreground mt-0.5">Date: 06 September 2026</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b-2 border-border/60">
                          {['Flock', 'Opening', 'In', 'Transfers In', 'Mortality', 'Cull', 'Sold', 'Out', 'Closing'].map((h) => (
                            <th key={h} className="text-center py-2 px-2 text-muted-foreground font-semibold whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { flock: 'BR-2026-002', opening: 4617, in: 0, tIn: 0, mort: 3, cull: 0, sold: 0, out: 0, closing: 4614 },
                          { flock: 'BR-2026-003', opening: 3436, in: 0, tIn: 0, mort: 4, cull: 0, sold: 0, out: 0, closing: 3432 },
                          { flock: 'LY-2026-001', opening: 7744, in: 0, tIn: 0, mort: 2, cull: 0, sold: 0, out: 0, closing: 7742 },
                          { flock: 'LY-2026-002', opening: 6318, in: 0, tIn: 0, mort: 0, cull: 0, sold: 0, out: 0, closing: 6318 },
                        ].map((row) => (
                          <tr key={row.flock} className="border-b border-border/40">
                            <td className="py-1.5 px-2 font-medium text-primary">{row.flock}</td>
                            <td className="py-1.5 px-2 font-tabular text-center text-foreground">{row.opening.toLocaleString()}</td>
                            <td className="py-1.5 px-2 font-tabular text-center text-green-600">{row.in}</td>
                            <td className="py-1.5 px-2 font-tabular text-center text-blue-600">{row.tIn}</td>
                            <td className="py-1.5 px-2 font-tabular text-center text-red-600">{row.mort}</td>
                            <td className="py-1.5 px-2 font-tabular text-center text-orange-600">{row.cull}</td>
                            <td className="py-1.5 px-2 font-tabular text-center text-purple-600">{row.sold}</td>
                            <td className="py-1.5 px-2 font-tabular text-center text-amber-600">{row.out}</td>
                            <td className="py-1.5 px-2 font-tabular text-center font-semibold text-foreground">{row.closing.toLocaleString()}</td>
                          </tr>
                        ))}
                        <tr className="border-t-2 border-border/60 bg-muted/20 font-bold">
                          <td className="py-1.5 px-2 text-foreground">TOTAL</td>
                          <td className="py-1.5 px-2 font-tabular text-center text-foreground">22,115</td>
                          <td className="py-1.5 px-2 font-tabular text-center text-green-600">0</td>
                          <td className="py-1.5 px-2 font-tabular text-center text-blue-600">0</td>
                          <td className="py-1.5 px-2 font-tabular text-center text-red-600">9</td>
                          <td className="py-1.5 px-2 font-tabular text-center text-orange-600">0</td>
                          <td className="py-1.5 px-2 font-tabular text-center text-purple-600">0</td>
                          <td className="py-1.5 px-2 font-tabular text-center text-amber-600">0</td>
                          <td className="py-1.5 px-2 font-tabular text-center font-bold text-foreground">22,106</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3 text-center">
                    Generated by Y77 Poultry Management System · {new Date().toLocaleDateString('en-GB')}
                  </p>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
